
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Task as TaskType, Reward as RewardType, getCurrentTimestamp, AppData } from '@/utils/storage';
import { EditForm } from '@/components/EditTaskRewardDialog';
import { useStarData } from '@/hooks/useStarData';

const EditItemPage: React.FC = () => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const location = useLocation();
  const { data, updateData } = useStarData();
  const fromEditMode = location.state?.fromEditMode === true;
  
  const [editForm, setEditForm] = useState<EditForm>({
    title: '',
    description: '',
    stars: 1,
    cost: 10,
    daily: false,
    resetInterval: 0
  });
  
  const isEditingTask = type === 'task';
  const isAddingNew = id === 'new';
  
  const resetIntervalOptions = [
    { value: 0, label: 'No auto-reset' },
    { value: 60, label: 'Every hour' },
    { value: 180, label: 'Every 3 hours' },
    { value: 360, label: 'Every 6 hours' },
    { value: 720, label: 'Every 12 hours' },
    { value: 1440, label: 'Every day' },
    { value: 10080, label: 'Every week' }
  ];
  
  const navigateBack = useCallback(() => {
    // Navigate back to the main page with the appropriate tab selected and edit mode preserved
    const tab = isEditingTask ? 'tasks' : 'rewards';
    navigate('/', { 
      state: { 
        keepEditMode: fromEditMode,
        initialTab: tab 
      } 
    });
  }, [navigate, isEditingTask, fromEditMode]);
  
  useEffect(() => {
    if (!data) return;
    
    if (!isAddingNew) {
      if (isEditingTask) {
        const task = data.tasks.find(t => t.id === id);
        if (task) {
          setEditForm({
            title: task.title,
            description: task.description || '',
            stars: task.stars,
            cost: 10,
            daily: task.daily,
            resetInterval: task.resetInterval || 0
          });
        } else {
          toast.error('Task not found');
          navigateBack();
        }
      } else {
        const reward = data.rewards.find(r => r.id === id);
        if (reward) {
          setEditForm({
            title: reward.title,
            description: reward.description || '',
            stars: 1,
            cost: reward.cost,
            daily: reward.daily || false,
            resetInterval: 0
          });
        } else {
          toast.error('Reward not found');
          navigateBack();
        }
      }
    } else {
      // Set defaults for new items
      setEditForm({
        title: '',
        description: '',
        stars: isEditingTask ? 1 : 0,
        cost: isEditingTask ? 0 : 10,
        daily: isEditingTask,
        resetInterval: 0
      });
    }
  }, [id, isAddingNew, isEditingTask, data, navigateBack]);
  
  const handleSaveEdit = () => {
    if (!data) return;
    
    if (isAddingNew) {
      if (isEditingTask) {
        const maxOrder = data.tasks.reduce(
          (max, task) => Math.max(max, task.order || 0), 
          -1
        );
        
        const newTask: TaskType = {
          id: Date.now().toString(),
          title: editForm.title,
          stars: editForm.stars,
          completed: false,
          daily: editForm.daily,
          order: maxOrder + 1,
          description: editForm.description || undefined,
          resetInterval: editForm.resetInterval && editForm.resetInterval > 0 
            ? editForm.resetInterval 
            : undefined,
          lastReset: editForm.resetInterval && editForm.resetInterval > 0 
            ? getCurrentTimestamp() 
            : undefined
        };
        
        const newData: AppData = { ...data, tasks: [...data.tasks, newTask] };
        updateData(newData);
        toast.success('New task added!');
      } else {
        const newReward: RewardType = {
          id: Date.now().toString(),
          title: editForm.title,
          description: editForm.description || undefined,
          cost: editForm.cost,
          claimed: false,
          daily: editForm.daily,
          lastResetDate: editForm.daily ? new Date().toISOString().split('T')[0] : undefined
        };
        
        const newData: AppData = { ...data, rewards: [...data.rewards, newReward] };
        updateData(newData);
        toast.success('New reward added!');
      }
    } else {
      if (isEditingTask) {
        const taskIndex = data.tasks.findIndex(t => t.id === id);
        if (taskIndex >= 0) {
          const oldTask = data.tasks[taskIndex];
          const updatedTask: TaskType = {
            ...oldTask,
            title: editForm.title,
            description: editForm.description || undefined,
            stars: editForm.stars,
            daily: editForm.daily,
            resetInterval: editForm.resetInterval && editForm.resetInterval > 0 
              ? editForm.resetInterval 
              : undefined,
            lastReset: (editForm.resetInterval && editForm.resetInterval > 0 && !oldTask.resetInterval) 
              ? getCurrentTimestamp() 
              : oldTask.lastReset
          };
          
          const updatedTasks = [...data.tasks];
          updatedTasks[taskIndex] = updatedTask;
          const newData: AppData = { ...data, tasks: updatedTasks };
          updateData(newData);
          toast.success('Task updated!');
        }
      } else {
        const rewardIndex = data.rewards.findIndex(r => r.id === id);
        if (rewardIndex >= 0) {
          const oldReward = data.rewards[rewardIndex];
          const updatedReward: RewardType = {
            ...oldReward,
            title: editForm.title,
            description: editForm.description || undefined,
            cost: editForm.cost,
            daily: editForm.daily,
            lastResetDate: editForm.daily && !oldReward.daily ? new Date().toISOString().split('T')[0] : oldReward.lastResetDate
          };
          
          const updatedRewards = [...data.rewards];
          updatedRewards[rewardIndex] = updatedReward;
          const newData: AppData = { ...data, rewards: updatedRewards };
          updateData(newData);
          toast.success('Reward updated!');
        }
      }
    }
    
    navigateBack();
  };
  
  if (!data) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <div className="animate-pulse-scale">
          <div className="text-4xl">⭐</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-[100dvh] p-4 sm:p-6 pt-[calc(1rem+env(safe-area-inset-top,0px))] pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pl-[calc(1rem+env(safe-area-inset-left,0px))] pr-[calc(1rem+env(safe-area-inset-right,0px))]">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={navigateBack}
            className="mr-2"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold">
            {isAddingNew ? `Add New ${isEditingTask ? 'Task' : 'Reward'}` : `Edit ${isEditingTask ? 'Task' : 'Reward'}`}
          </h1>
        </div>
        
        <div className="space-y-6 bg-card rounded-xl p-6 shadow-md">
          <div className="space-y-3">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              placeholder="Enter title"
            />
          </div>
          
          <div className="space-y-3">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Enter description"
              rows={3}
            />
          </div>
          
          {isEditingTask ? (
            <>
              <div className="space-y-3">
                <Label htmlFor="stars">Stars</Label>
                <Input
                  id="stars"
                  type="number"
                  min="1"
                  value={editForm.stars}
                  onChange={(e) => setEditForm({ ...editForm, stars: parseInt(e.target.value) || 1 })}
                />
              </div>
              
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="daily"
                  checked={editForm.daily}
                  onCheckedChange={(checked) => 
                    setEditForm({ 
                      ...editForm, 
                      daily: checked === true 
                    })
                  }
                />
                <Label htmlFor="daily" className="cursor-pointer">Daily task (resets each day)</Label>
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="resetInterval">Auto-reset interval</Label>
                <Select
                  value={String(editForm.resetInterval || 0)}
                  onValueChange={(value) => setEditForm({ 
                    ...editForm, 
                    resetInterval: parseInt(value)
                  })}
                >
                  <SelectTrigger id="resetInterval">
                    <SelectValue placeholder="Select auto-reset interval" />
                  </SelectTrigger>
                  <SelectContent>
                    {resetIntervalOptions.map(option => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This will automatically reset the task completion after the specified time interval.
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Reward Type Toggle */}
              <div className="space-y-3 border-b pb-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">Reward Type</Label>
                    <p className="text-xs text-muted-foreground">
                      Choose between star-based or monetary rewards
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Label htmlFor="reward-type-toggle" className="text-sm">
                      {isMonetaryReward ? 'Money Reward' : 'Star Reward'}
                    </Label>
                    <Switch
                      id="reward-type-toggle"
                      checked={isMonetaryReward}
                      onCheckedChange={setIsMonetaryReward}
                    />
                  </div>
                </div>
              </div>

              {/* Conditional Form Fields Based on Reward Type */}
              {isMonetaryReward ? (
                /* Monetary Reward Fields */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-3">
                      <Label htmlFor="currency">Currency</Label>
                      <Select
                        value={editForm.currency || '$'}
                        onValueChange={(value) => setEditForm({ 
                          ...editForm, 
                          currency: value
                        })}
                      >
                        <SelectTrigger id="currency">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="$">$ (Dollar)</SelectItem>
                          <SelectItem value="€">€ (Euro)</SelectItem>
                          <SelectItem value="£">£ (Pound)</SelectItem>
                          <SelectItem value="¥">¥ (Yen)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="cost">Cost (money)</Label>
                      <Input
                        id="cost"
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.cost}
                        onChange={(e) => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Set the monetary cost for this reward.
                  </p>
                </div>
              ) : (
                /* Star Reward Fields */
                <div className="space-y-3">
                  <Label htmlFor="cost">Cost (stars)</Label>
                  <Input
                    id="cost"
                    type="number"
                    min="1"
                    value={editForm.cost}
                    onChange={(e) => setEditForm({ ...editForm, cost: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Set the number of stars required to claim this reward.
                  </p>
                </div>
              )}
              
              {/* Daily reward checkbox */}
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="daily-reward"
                  checked={editForm.daily}
                  onCheckedChange={(checked) => 
                    setEditForm({ 
                      ...editForm, 
                      daily: checked === true 
                    })
                  }
                />
                <Label htmlFor="daily-reward" className="cursor-pointer">Daily reward (resets each day at midnight)</Label>
              </div>
            </>
          )}
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={navigateBack} className="mr-2">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="flex items-center gap-1">
              <Check size={16} />
              Save {isEditingTask ? 'Task' : 'Reward'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditItemPage;
